import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  effect,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { CurrencyAmountStateAccessor } from '../services/currency-amount-state.accessor';
import { paymentValidationsForSEPA } from '@finxone-platform/shared/frontend';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  AccountInfoType,
  AccountTypes,
  FormFieldConfig,
  InputLabelOptions,
  SavingsAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { formatCurrency } from '@angular/common';
import { isArray } from 'lodash';
import { Subject, map, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { AngularValidationType, ValidationConfig } from '../input-field-validation/validation-config.type';

// Determine whether the browser supports the field-sizing CSS property
/**
 * Check for undefined here because we are trying to use
 * CSS.supports() at the module level, but the CSS global object doesn't exist in the Jest testing environment.
 */
const isFieldSizingSupported = typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content');

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-currency-amount-input',
  templateUrl: './currency-amount-input.component.html',
  styleUrls: ['./currency-amount-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneCurrencyAmountInputComponent),
      multi: true,
    },
  ],
})
export class FinxoneCurrencyAmountInputComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);
  private readonly stateAccessor = inject(CurrencyAmountStateAccessor);

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() inputFieldConfig: FormFieldConfig;
  // Validation Configuration
  @Input() validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;
  @Input() public formControlInvalid = false;
  @Input() public id = '';
  @Input() public name = '';
  @Input() public value = '';
  @Input() public infoText = '';
  @Input() public inputLabelTooltipText = '';
  @Input() public textStyleClass = 'h6';
  @Input() public isDisabled = false;
  @Input() public isInputOnFocus = false;
  @Input() public hasPrefixContent = false;
  @Input() public customErrorTemplate?: TemplateRef<unknown>;
  @Input() set accountTypeContext(value: AccountTypes) {
    this.setupAccountsSubscription(value);
  }
  @Input() public disableAutocomplete = false;
  @Input() parentControl?: FormControl;

  public control: FormControl = new FormControl('');

  // Used for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  private cssVars: Record<string, string> = {};
  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public inputIdleColor: string;
  public inputActiveColor: string;
  public selectedAccount: AccountInfoType | SavingsAccountInfoType | undefined;
  public selectedMultiCurrencyCode: string | undefined;
  public paymentValidations: {
    maximumTransactionLimit?: number | undefined;
    beneficiaryAddressRequired?: boolean | undefined;
    beneficiaryBICRequired?: boolean | undefined;
  };
  public currency: { symbol: string; code: string };
  public formattedValue = '';
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';
  public borderRadiusClass = '';

  constructor() {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        //get the label type and colors from root styles
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
        this.inputIdleColor = this.getCssVariableValue('--inputFieldIdleTextColor') as string;
        this.inputActiveColor = this.getCssVariableValue('--inputFieldActiveTextColor') as string;
        this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
        this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
      }
    });
  }

  private setupAccountsSubscription(accountType: AccountTypes) {
    if (accountType === AccountTypes.CURRENT_ACCOUNT) {
      this.stateAccessor
        .getCurrentAccount()
        .pipe(
          takeUntil(this.destroy$),
          map((accountData) => {
            this.selectedAccount = accountData;
            this.selectedMultiCurrencyCode = accountData?.currentMultiCurrencyCode;
            this.updateCurrency();
          }),
        )
        .subscribe();
    } else if (accountType === AccountTypes.SAVINGS_ACCOUNT) {
      this.stateAccessor
        .getCurrentSavingsAccount()
        .pipe(
          takeUntil(this.destroy$),
          map((accountData) => {
            this.selectedAccount = accountData;
            this.updateCurrency();
          }),
        )
        .subscribe();
    } else {
      // Use default currency if no account type is provided
      this.selectedAccount = undefined;
      this.updateCurrency();
    }
  }

  // Extract the currency update logic to a separate method
  private updateCurrency(): void {
    let code: string | undefined = isArray(this.selectedAccount?.currency)
      ? (this.selectedMultiCurrencyCode ?? '')
      : (this.selectedAccount?.currency ?? '');
    if (!code || code === '-') {
      code = (this.inputFieldConfig['defaultCurrency'] as string) ?? undefined;
    }
    if (!code || code === '-') {
      code = 'GBP';
    }
    // Take only the first part if code contains " / " e.g. "USD / ESP"
    if (code.includes(' / ')) {
      code = code.split(' / ')[0].trim();
    }
    let symbol = '';
    try {
      symbol =
        Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
        })
          .formatToParts()
          .find((part) => part.type === 'currency')?.value ?? '';
    } catch {
      symbol = '';
    }
    // If code is invalid (XXX, -) or symbol is missing or equals code, show XXX and ¤
    if (!symbol || symbol === code || code === 'XXX' || code === '-') {
      this.currency = { code: 'XXX', symbol: '¤' };
    } else {
      this.currency = { code, symbol };
    }
  }

  ngOnInit() {
    if (this.parentControl) this.control = this.parentControl;
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    if (this.control?.value) {
      this.formattedValue = formatCurrency(
        this.control.value.toString(),
        'en-GB',
        '', // empty string - no symbol
        this.currency.code, // currency code to determine decimal places
        undefined, // digitsInfo undefined - use currency default decimals
      );
    }
    this.setDynamicWidth();
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageForField(validator: ValidationConfig): string {
    return this.getErrorMessage(validator);
  }

  /**
   * Handles input field changes, performs validation, and triggers appropriate form submission logic.
   */
  public handleOnChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const rawValue = inputElement.value;

    // Remove all non-numeric characters except decimal point
    const cleanedValue = rawValue.replace(/[^0-9.]/g, '');

    // If the cleaned value is different from the raw value, update the input
    if (cleanedValue !== rawValue) {
      this.control?.setValue(cleanedValue);
      // Set cursor position to the end after cleaning
      setTimeout(() => {
        inputElement.setSelectionRange(cleanedValue.length, cleanedValue.length);
      }, 0);
    }

    const parsedValue = parseFloat(cleanedValue);
    const inputValue = formatCurrency(
      parsedValue,
      'en-GB',
      '', // empty string - no symbol
      this.currency.code, // currency code to determine decimal places
      undefined, // digitsInfo undefined - use currency default decimals
    );

    // If the value is empty or NaN, set to empty string and return
    if (!cleanedValue || isNaN(parseFloat(cleanedValue)) || !inputValue) {
      this.control?.setValue('');
      this.onChange();
      this.formattedValue = '';
      return;
    }

    // Reset the input and submit the form if the value is negative
    if (parsedValue < 0) {
      this.control?.setValue('');
      return;
    }
    this.formattedValue = inputValue;

    /**
     * Perform SEPA-specific validations if the payment flow is SEPA
     * @default {EURO}
     */
    if (this.selectedAccount?.currency === 'EUR') {
      this.validateSEPATransactionLimits(parsedValue);
      return;
    }
  }

  /**
   * Validates the SEPA transaction limits based on the input value and payment rail configuration.
   * If the input value exceeds defined limits, appropriate errors are set on the form field.
   *
   * @param inputValue - The amount to validate against SEPA transaction limits.
   */
  private validateSEPATransactionLimits(inputValue: number): void {
    // Retrieve the SEPA payment rail type from the state
    const paymentRail = this.stateAccessor.getSepaPaymentRail();

    // Proceed with validation only if a valid payment rail type is configured
    if (paymentRail) {
      this.paymentValidations = paymentValidationsForSEPA[paymentRail];

      // Check for maximum transaction limit violation (if defined)
      if (
        this.paymentValidations?.maximumTransactionLimit &&
        inputValue > this.paymentValidations.maximumTransactionLimit
      ) {
        this.handleTransactionLimitError(Validators.max(this.paymentValidations?.maximumTransactionLimit));
        return;
      }

      // Clear errors and validate the form field when within limits
      this.clearTransactionLimitErrors();
    }
  }

  /**
   * Handles form validation errors for transaction limits by setting appropriate error messages.
   *
   * @param errorKey - The key representing the type of error (e.g., 'invalidMinimumAmount').
   */
  private handleTransactionLimitError(validators: ValidatorFn): void {
    this.control?.setValidators(validators); // Set the error
    this.control?.updateValueAndValidity(); // Trigger form validation
  }

  /**
   * Clears any transaction limit validation errors on the form field.
   */
  private clearTransactionLimitErrors(): void {
    this.control?.clearAsyncValidators();
    this.control?.updateValueAndValidity(); // Trigger form validation
  }

  /**
   * Adjusts the width of the input field based on the length of the value.
   * Needed for large variant when field-sizing property is not supported.
   */
  public setDynamicWidth() {
    if (!isFieldSizingSupported && this.inputFieldConfig?.inputStyle === 'large') {
      const inputField = document.getElementById(this.inputFieldConfig?.id ?? '') as HTMLInputElement;
      if (inputField) {
        const valueToMeasure = this.formattedValue || '0.00';

        // Use canvas to measure actual text width
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (context) {
          // Get computed font from the input element
          const computedStyle = window.getComputedStyle(inputField);
          context.font = computedStyle.font;

          // Measure the text width
          const metrics = context.measureText(valueToMeasure);
          const textWidth = Math.ceil(metrics.width);

          // Add buffer (4-6px) to prevent clipping, especially for decimal places
          const bufferWidth = 6;
          inputField.style.width = `${textWidth + bufferWidth}px`;
        } else {
          // Fallback to ch-based calculation with extra buffer
          inputField.style.width = `calc(${
            !inputField?.value.length ? 4 : this.formattedValue.length
          } * .84ch + 6px)`;
        }
      }
    }
  }

  public validationConfigContainsKey(key: string): boolean {
    return this.validationConfig.some((config) => config.type === key);
  }

  public get maxLength(): number {
    const maxLengthConfig = this.validationConfig.find(
      (config) => config.type === AngularValidationType.MaxLength,
    );
    // maxLength is set to 22 the maximum number of digits before it converts to scientific display e.g. '3.33E+22'
    return Number(maxLengthConfig?.params) || 22;
  }

  public handleBlurEvent() {
    this.onTouched();
    this.isInputOnFocus = false;
    this.setDynamicWidth();
    if (!this.control?.value) {
      this.onChange('');
      return;
    }
    const val = this.formattedValue.replace(/,/g, '') || '';
    this.onChange(val);
  }
}
